import { Controller } from "@hotwired/stimulus"

// Debounced autosave + live pricing for the quote form.
//
// - Persisted quote (edit): each change PATCHes the form to the update action,
//   which saves the record AND returns a turbo_stream replacing #quote-totals.
//   A status line reflects saving / saved / error.
// - New quote (not yet persisted): there's no record to save to, so we fall
//   back to the calculate endpoint for a live preview until the user creates it.
export default class extends Controller {
  static targets = ["status", "skipOnboarding", "onboardingFields", "onboardingTotals", "onboardingSkippedNote", "submit"]
  static values = {
    url: String,                                  // calculate (preview) endpoint
    saveUrl: String,                              // update (autosave) endpoint; blank until persisted
    persisted: { type: Boolean, default: false },
    debounceMs: { type: Number, default: 400 }
  }

  connect() {
    this.toggleSkipOnboarding()
  }

  // The switch reads "include onboarding", so checked means not skipped; the
  // hidden field beside it submits skip_onboarding=1 whenever it is off.
  toggleSkipOnboarding() {
    const skipped = this.hasSkipOnboardingTarget && !this.skipOnboardingTarget.checked
    this.onboardingFieldsTargets.forEach((el) => el.classList.toggle("d-none", skipped))
    this.onboardingTotalsTargets.forEach((el) => el.classList.toggle("d-none", skipped))
    this.onboardingSkippedNoteTargets.forEach((el) => el.classList.toggle("d-none", !skipped))
    this.relabelSubmit(skipped)
  }

  // A new calculator writes one quote row or two depending on the switch, and
  // the button says which.
  relabelSubmit(skipped) {
    if (!this.hasSubmitTarget) return
    const { withOnboardingLabel, withoutOnboardingLabel } = this.submitTarget.dataset
    if (!withOnboardingLabel || !withoutOnboardingLabel) return
    this.submitTarget.value = skipped ? withoutOnboardingLabel : withOnboardingLabel
  }

  disconnect() {
    if (this.timer) clearTimeout(this.timer)
    if (this.inflight) this.inflight.abort()
  }

  recalc() {
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => this.submit(), this.debounceMsValue)
    this.flagLoading(true)
    if (this.persistedValue) this.setStatus("saving")
  }

  async submit() {
    const data = new FormData(this.element)
    const autosaving = this.persistedValue && this.saveUrlValue

    let url
    if (autosaving) {
      // Keep the hidden _method=patch so Rails routes this to #update.
      url = this.saveUrlValue
    } else {
      // The calculate route is its own POST; drop the PATCH override.
      data.delete("_method")
      url = this.urlValue
    }

    // Supersede any in-flight save so responses can't arrive out of order and
    // render stale totals — only the latest edit's response is applied.
    if (this.inflight) this.inflight.abort()
    const ac = new AbortController()
    this.inflight = ac

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Accept": "text/vnd.turbo-stream.html, text/html",
          "X-CSRF-Token": this.csrfToken(),
          "X-Requested-With": "XMLHttpRequest"
        },
        body: data,
        credentials: "same-origin",
        signal: ac.signal
      })

      if (!response.ok) {
        console.error("[sales-quote-form] save/recalc failed:", response.status)
        this.flagLoading(false)
        if (autosaving) this.setStatus("error")
        return
      }

      const html = await response.text()
      if (window.Turbo && typeof window.Turbo.renderStreamMessage === "function") {
        window.Turbo.renderStreamMessage(html)
      } else {
        this.applyStreamManually(html)
      }
      this.flagLoading(false)
      if (autosaving) this.setStatus("saved")
    } catch (err) {
      if (err.name === "AbortError") return // superseded by a newer edit
      console.error("[sales-quote-form] save/recalc error:", err)
      this.flagLoading(false)
      if (autosaving) this.setStatus("error")
    } finally {
      if (this.inflight === ac) this.inflight = null
    }
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  setStatus(state) {
    if (!this.hasStatusTarget) return
    const states = {
      saving: ['<i class="fa-regular fa-spinner fa-spin me-1"></i>Saving…', "text-muted"],
      saved: ['<i class="fa-regular fa-circle-check me-1"></i>All changes saved', "text-success"],
      error: ['<i class="fa-regular fa-triangle-exclamation me-1"></i>Couldn’t save — use the Save button', "text-danger"]
    }
    const [html, cls] = states[state] || ["", "text-muted"]
    this.statusTarget.innerHTML = html
    this.statusTarget.className = `small text-center ${cls}`
  }

  flagLoading(isLoading) {
    const totals = document.getElementById("quote-totals")
    if (!totals) return
    totals.style.opacity = isLoading ? "0.6" : "1"
    totals.style.transition = "opacity 120ms ease"
  }

  // Minimal turbo-stream parser for environments without Turbo on the page.
  applyStreamManually(html) {
    const tpl = document.createElement("template")
    tpl.innerHTML = html.trim()
    tpl.content.querySelectorAll("turbo-stream").forEach((s) => {
      const action = s.getAttribute("action")
      const target = s.getAttribute("target")
      const template = s.querySelector("template")
      if (!template) return
      const dest = document.getElementById(target)
      if (!dest) return
      if (action === "replace") dest.outerHTML = template.innerHTML
      else if (action === "update") dest.innerHTML = template.innerHTML
    })
  }
}
